import React from 'react';
import classnames from 'classnames';
import Lottie from 'react-lottie';
import { gikClassPrefix } from '@gik/core/constants';
import { formatCurrency, formatDate } from '@gik/core/utils/format';
import { parsePaymentMethodName } from '../../utils/productUtils';
import Collapse from '@gik/ui/Collapse';
import { PaymentConfirmationValues } from '../../types';
import orderCardAnimation from '../../animations/orderCard.json';
import orderGiftboxAnimation from '../../animations/orderGiftbox.json';
import { Button, Decoder, LottieOptions } from '@gik/ui';
import { useProducts } from '../../api/products';
import { authModals } from '@gik/auth';
import { useUserStore } from '@gik/core/store/UserStore';
import { CalloutBlockType } from '@gik/auth/utils/auth';

export interface PaymentConfirmationProps extends React.HTMLAttributes<HTMLDivElement> {
  values: PaymentConfirmationValues;
  productName: string;
  shipping?: boolean;
  header?: React.ReactNode;
  successIcon?: React.ReactNode;
  renderDefaultThankYou?: boolean;
  renderFullfillmentDescription?: (fulfillmentTimeString: string) => React.ReactNode;
}

const blockName = `${gikClassPrefix}-payment-confirmation`;

export default function PaymentConfirmation({
  className,
  values,
  productName,
  shipping,
  header,
  successIcon,
  renderDefaultThankYou = true,
  renderFullfillmentDescription = fulfillmentTimeString => (
    <>&nbsp;and is typically fulfilled and sent to the recipient in {fulfillmentTimeString}.</>
  ),
  ...otherProps
}: PaymentConfirmationProps): React.ReactElement {
  const productIds = values.products ? values.products.map(item => item.productId) : [];
  const { data: products } = useProducts(productIds);

  const userId = useUserStore(state => state.id);

  const blockClasses = classnames([blockName, className || '']);

  const shippingDetails = values.shipping;
  const paymentMethod = parsePaymentMethodName(values.paymentMethod);

  const orderCardAnimationOptions = LottieOptions.looped(orderCardAnimation);
  const giftboxAnimationOptions = LottieOptions.looped(orderGiftboxAnimation);

  // get the checkoutType from the first product
  const checkoutType = values.products[0].checkoutType;
  const animation = checkoutType === 'tango-card' ? orderCardAnimationOptions : giftboxAnimationOptions;

  const fulfillmentTimeString = checkoutType === 'tango-card' ? '24 hours' : '1-2 business days';

  function onClickSignupForUpdates() {
    authModals.openSignupModal({
      calloutBlock: CalloutBlockType.UPDATES,
      recipientName: `${shippingDetails.firstName} ${shippingDetails.lastName}`.trim(),
    });
  }

  return (
    <div className={blockClasses} {...otherProps}>
      {header}
      {renderDefaultThankYou && (
        <div className="tw-text-center">
          {successIcon || (
            <div className="gik-circle gik-circle--primary gik-circle--2xl">
              <Lottie options={animation} isPaused={false} />
            </div>
          )}
          <span className={`${blockName}__section-title`}>Thanks for your order!</span>
          <p className={`${blockName}__section-text-body`}>
            Your&nbsp;
            <strong>
              <Decoder text={productName} />
            </strong>
            &nbsp;is on its way to{' '}
            <strong>
              {checkoutType === 'tango-card'
                ? shippingDetails.email
                : `${shippingDetails.firstName} ${shippingDetails.lastName}`.trim()}
            </strong>
            {renderFullfillmentDescription(fulfillmentTimeString)}
          </p>

          {!userId && (
            <>
              <p className={`${blockName}__section-text-body`}>
                Stay in the loop. Get updates from this page so you know how best to support{' '}
                <strong>{`${shippingDetails.firstName} ${shippingDetails.lastName}`.trim()}</strong> in the future.
              </p>

              <Button className={`${blockName}__section-text-body`} onClick={onClickSignupForUpdates}>
                Sign up for updates
              </Button>
            </>
          )}
        </div>
      )}

      <ul className="gik-tablelist">
        <li>
          <label>Order number:</label>
          <strong>{values.orderId}</strong>
        </li>
        <li>
          <label>Date:</label>
          <strong>{formatDate(values.orderDate)}</strong>
        </li>
        <li>
          <label>Email:</label>
          <strong>{values.senderEmail}</strong>
        </li>
        <li>
          <label>Total:</label>
          <strong>{formatCurrency(values.total)}</strong>
        </li>
        <li>
          <label>Payment method:</label>
          <strong>{paymentMethod}</strong>
        </li>
      </ul>

      <br />

      <Collapse className={`${blockName}__collapse`} title="Show Order Details" visibleTitle="Hide Order Details">
        <ul className="gik-datalist">
          {products &&
            values.products &&
            values.products.map((item, index) => {
              // find product based on the order's productId
              const product = products.find(product => product.id === item.productId);
              if (!product) {
                return <li key={`notfound-${index}`}>Product not found...</li>;
              }
              return (
                <li key={item.productId}>
                  <label>
                    <strong>
                      <Decoder text={product.name} /> <strong>x{item.quantity || 1}</strong>
                    </strong>
                  </label>
                  <span>
                    <strong>{formatCurrency(item.price)}</strong>
                  </span>
                </li>
              );
            })}
          <li className="gik-datalist__item--highlight">
            <label>
              <strong>Subtotal:</strong>
            </label>
            <span>{formatCurrency(values.subtotal)}</span>
          </li>
          {values.taxCost !== undefined && (
            <li className="gik-datalist__item--highlight">
              <label>
                <strong>Taxes:</strong>
              </label>
              <span>{formatCurrency(values.taxCost)}</span>
            </li>
          )}
          {values.tip !== undefined && (
            <li className="gik-datalist__item--highlight">
              <label>
                <strong>Give InKind Tip:</strong>
              </label>
              <span>{formatCurrency(values.tip)}</span>
            </li>
          )}
          {values.shippingCost !== undefined && (
            <li className="gik-datalist__item--highlight">
              <label>
                <strong>Shipping:</strong>
              </label>
              <span>{formatCurrency(values.shippingCost)}</span>
            </li>
          )}
          {values.shippingTax !== undefined && (
            <li className="gik-datalist__item--highlight">
              <label>
                <strong>Shipping Tax:</strong>
              </label>
              <span>{formatCurrency(values.shippingTax)}</span>
            </li>
          )}
          <li className="gik-datalist__item--highlight">
            <label>
              <strong>Payment method:</strong>
            </label>
            <span>{paymentMethod}</span>
          </li>
          <li className="gik-datalist__item--highlight">
            <label>
              <strong>Total:</strong>
            </label>
            <span>{formatCurrency(values.total)}</span>
          </li>
        </ul>
      </Collapse>

      {shipping && shippingDetails && (
        <section>
          <span className="gik-payment-confirmation__shipping-title">Shipping to:</span>
          <address>
            <strong>
              {shippingDetails.firstName} {shippingDetails.lastName}
            </strong>
            <br />
            {shippingDetails.address1} <br />
            {shippingDetails.address2 && (
              <>
                {shippingDetails.address2} <br />
              </>
            )}
            {shippingDetails.city}, {shippingDetails.state} {shippingDetails.postalCode} <br />
          </address>
        </section>
      )}
    </div>
  );
}
